import React, { useState, useEffect } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';
import { getStatusColor } from '../StatusSelect';
import ProductInstructions from './ProductInstructions';
import { enumInventoryPhasedStatus } from '../../consts/inventoryStatus';
import { addProductCategoriesOptionRequest, getProductCategoriesOptionsRequest, ProductCategoriesLoadingKeys, updateProductCategoriesByProductRequest, updateVendorByProductRequest } from '../../slices/productCategoriesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ValueSelectByObject } from '../ValueSelectByObject';
import { addVariantToGroup, clearVariantsByGroup, deleteVariantFromGroup, downloadAllBarcodes, getSuppliers, getVariantsByGroup, ProductGroupLoadingKeys, updateBOMVariantByGroup, updateGroupName, updateLeadTimeByGroup, updateSingleGroup, updateSingleProductInGroup } from '../../slices/productGroupSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import printJS from 'print-js';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { arrayHasItems } from '../../helpers/common';
import ProductBillOfMaterials from './ProductBillOfMaterials';
import VariantModal from './VariantModal';
import { ConnectVariantButton } from '../common/ConnectVariantButton';
import MOQ from './MOQ';
import { BOMVariant } from './BOMVariant';
import { SelectDropdown } from '../SelectDropdown';

const ProductSidebar = ({ 
    product, 
    access,
    selectedTab,
    onChangeSelectedTab
}) => {
    const [activeTab, setActiveTab] = useState(selectedTab);
    const [leadTimeValue, setLeadTimeValue] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [productNameValue, setProductNameValue] = useState(null);
    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
    const [groupVariantModalData, setGroupVariantModalData] = useState(null);
    const dispatch = useDispatch();
    const {
        loading: groupLoading,
        loadingKeys: groupLoadingKeys,
        searchedVariants: searchedVariantsByGroup,
        vendorsToSelect,
        variantsByGroup
    } = useSelector((state) => state.productGroup);
    const {
        loading: productCategoryLoading,
        loadingKeys: productCategoryKeys,
        options: productCategoriesOptions
    } = useSelector((state) => state.productCategories);

    const handlePrintBarcode = async () => {
        const resultAction = await dispatch(downloadAllBarcodes({
            groupId: product.id
        }));
        const res = unwrapResult(resultAction);
        const newTab = window.open(res.url, '_blank');
        if (newTab) {
            newTab.focus();
        }
        // printJS({
        //     printable: res.url,
        //     type: 'pdf' ,
        //     multiple: true
        // });
    }

    useEffect(() => {
        dispatch(getProductCategoriesOptionsRequest({}))
        return () => {
            dispatch(clearVariantsByGroup({}))
        }
    }, []);

    useEffect(() => {
        dispatch(clearVariantsByGroup({}))
        dispatch(getVariantsByGroup({
            id: product?.id
        }))
        dispatch(getSuppliers({}))
    }, [product?.id]);

    useEffect(() => {
        setSelectedSupplier(product?.vendor_id)
    }, [product?.vendor_id])
    const handleDeleteVariant = async (productId) => {
        if (access === "edit") {
            await dispatch(deleteVariantFromGroup({
                productId,
                groupId: product.id,
                vendorId: product?.vendor_id,
                deleteManually: true
            }))
        }
    };

    const getSuppliersToSelectIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const downloadBarcodesByGroupIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.downloadBarcodes);
    const getVariantsByGroupIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariantsByGroup);
    const deleteVariantIsLoading = () => 
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.deleteVariant);
    const searchedVariantsIsLoading = () => 
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariants);
    const addVariantIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.addVariantToGroup);
    const productCategoriesIsLoading = () => 
        productCategoryLoading && productCategoryKeys.includes(ProductCategoriesLoadingKeys.getProductCategoriesOptions) || 
        productCategoryLoading && productCategoryKeys.includes(ProductCategoriesLoadingKeys.addProductCategoriesOptions) ||
        productCategoryLoading && productCategoryKeys.includes(ProductCategoriesLoadingKeys.updateProductCategoriesByProduct);
    const updateBOMVariantIsLoading = () => 
        productCategoryLoading && productCategoryKeys.includes(ProductGroupLoadingKeys.updateGroup);

    const productLeadTimeIsLoading = () => 
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.updateLeadTime);
        
    const productNameIsLoading = () => 
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.updateName);

    const handleAddVariant = async (variant) => {
        if (access === "edit") {
            await dispatch(addVariantToGroup({
                groupId: product.id,
                variantId: variant.id,
                vendorId: product.vendor_id
            }))
        }
    }

    const handleRemoveBOMVariant = async (sku) => {
        if (access === "edit") {
            await dispatch(updateBOMVariantByGroup({
                productId: product.id,
                bomProductSku: sku,
                requestToUpdate: updateSingleGroup,
                type: 'remove'
            }));
        }
    };

    const handleAddBOMVariant = async (variant) => {
        if (access === "edit") {
            await dispatch(updateBOMVariantByGroup({
                productId: product.id,
                bomProductSku: variant?.id,
                requestToUpdate: updateSingleGroup,
                type: 'add'
            }));
            setGroupVariantModalData(null);
        }
    }

    return (
        <div>
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-4">
                    <nav className="flex -mb-px">
                        <button
                            onClick={() => {
                                setActiveTab('details');
                                onChangeSelectedTab?.('group', 'details');
                            }}
                            className={`py-2 px-2 text-sm font-medium ${activeTab === 'details'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } focus:outline-none`}
                            aria-label="Show product details"
                        >
                            Details
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('bom');
                                onChangeSelectedTab?.('group', 'bom');
                            }}
                            className={`ml-4 py-2 px-2 text-sm font-medium ${activeTab === 'bom'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } focus:outline-none`}
                            aria-label="Show BOM"
                        >
                            BOM
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('variants');
                                onChangeSelectedTab?.('group', 'variants');
                            }}
                            className={`ml-4 py-2 px-2 text-sm font-medium ${activeTab === 'variants'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } focus:outline-none`}
                            aria-label="Show product variants"
                        >
                            Variants
                        </button>
                        {/* <button
                            onClick={() => setActiveTab('components')}
                            className={`ml-4 py-2 px-2 text-sm font-medium ${activeTab === 'components'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } focus:outline-none`}
                            aria-label="Show product components"
                        >
                            Components
                        </button> */}
                        {/* <button
                            onClick={() => {
                                setActiveTab('instructions');
                                onChangeSelectedTab?.('group', 'instructions');
                            }}
                            className={`ml-4 py-2 px-2 text-sm font-medium ${activeTab === 'instructions'
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } focus:outline-none`}
                            aria-label="Show product instructions"
                        >
                            Instructions
                        </button> */}
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'details' && (
                    <div>
                        {/* Product Image */}
                        <div className="mb-4">
                            <div className="w-full">
                                {product.image ? (
                                    <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center min-h-[300px]">
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="max-w-full max-h-48 object-contain min-h-48"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://placehold.co/300x200/e6e6e6/cccccc?text=No+Image';
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center min-h-[300px]">
                                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="border-t border-gray-200 pt-4 mt-2">
                            <dl className="divide-y divide-gray-200">
                                <div className="py-3 flex justify-between">
                                    <dt className="text-sm font-medium text-gray-500">Download all barcodes</dt>
                                    <dd className="text-sm text-gray-900">
                                        <div className="relative">
                                            <div className='flex items-center gap-2'>
                                                {downloadBarcodesByGroupIsLoading() && (
                                                    <LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={async () => await handlePrintBarcode()}
                                                    disabled={downloadBarcodesByGroupIsLoading()}
                                                    className="block whitespace-nowrap w-fit disabled:bg-gray-200 disabled:text-black items-center px-2.5 py-1.5 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    Download all
                                                </button>
                                            </div>
                                        </div>
                                    </dd>
                                </div>
                                <div className="py-3 flex justify-between">
                                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                                    <dd className="text-sm text-gray-900">
                                        <div className="relative">
                                            <div className='flex items-center gap-2'>
                                                {productNameIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                                                <input
                                                    value={productNameValue === null ? product.name : productNameValue}
                                                    onBlur={async (e) => {
                                                        if (access === "edit") {
                                                            if (e.target.value === product.name) return;
                                                            const editedValue = e.target.value.trim();
                                                            await dispatch(updateGroupName({
                                                                productId: product.id,
                                                                name: editedValue || "",
                                                                requestToUpdate: updateSingleGroup
                                                            }))
                                                            setProductNameValue(null);
                                                        }
                                                    }}
                                                    onKeyDown={async (e) => {
                                                        if (access === "edit") {
                                                            if (e.key === 'Enter') {
                                                                if (e.target.value === product.name) return;
                                                                e.preventDefault();
                                                                const editedValue = e.target.value.trim();
                                                                await dispatch(updateGroupName({
                                                                    productId: product.id,
                                                                    name: editedValue || "",
                                                                    requestToUpdate: updateSingleGroup
                                                                }))
                                                                setProductNameValue(null);
                                                            }
                                                        }
                                                    }}
                                                    disabled={productNameIsLoading() || access !== "edit"}
                                                    onChange={(e) => setProductNameValue(e.target.value)}
                                                    className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                                />
                                            </div>
                                        </div>
                                    </dd>
                                </div>
 
                                <div className="py-3 flex justify-between">
                                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                                    <dd className="text-sm text-gray-900">
                                        <ValueSelectByObject
                                            key={product.category_group_id}
                                            selectedValue={product.category_group_id}
                                            selectClassName={"w-52"}
                                            disabled={access !== "edit"}
                                            isLoading={productCategoriesIsLoading()}
                                            createNew={async (inputValue) => {
                                                if (access === "edit") {
                                                    await dispatch(addProductCategoriesOptionRequest({
                                                        value: inputValue
                                                    })).unwrap();  
                                                }
                                            }}
                                            options={productCategoriesOptions}
                                            onSelect={async (item) => {
                                                if (access === "edit") {
                                                    const categoryId = item?.value;
                                                    if (categoryId) {
                                                        try {
                                                            await dispatch(updateProductCategoriesByProductRequest({
                                                                productId: product.id, 
                                                                newCategory: categoryId,
                                                                requestToUpdate: updateSingleGroup
                                                            }))
                                                        } catch (error) {
                                                            console.error("Error updating Category by product:", error);
                                                        }    
                                                    }
                                                }
                                            }}
                                        />
                                    </dd>
                                </div>

                                <div className="py-3 flex justify-between">
                                    <dt className="text-sm font-medium text-gray-500">Lead Time</dt>
                                    <dd className="text-sm text-gray-900">
                                        <div className="relative">
                                            <div className='flex items-center gap-2'>
                                                {productLeadTimeIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                                                <input
                                                    value={leadTimeValue === null ? product.lead_time : leadTimeValue}
                                                    onBlur={async (e) => {
                                                        if (access === "edit") {
                                                            if (parseInt(e.target.value) === product.lead_time) return;
                                                            if (parseInt(e.target.value || 0) >= 0) {
                                                                // Implementation for saving lead time
                                                                await dispatch(updateLeadTimeByGroup({
                                                                    productId: product.id,
                                                                    leadTime: e.target.value || 0,
                                                                    requestToUpdate: updateSingleGroup
                                                                }))
                                                                setLeadTimeValue(null);
                                                            }
                                                        }
                                                    }}
                                                    onKeyDown={async (e) => {
                                                        if (access === "edit") {
                                                            if (e.key === 'Enter') {
                                                                if (parseInt(e.target.value) === product.lead_time) return;
                                                                e.preventDefault();
                                                                if (parseInt(e.target.value || 0) >= 0) {
                                                                    // Implementation for saving lead time
                                                                    await dispatch(updateLeadTimeByGroup({
                                                                        productId: product.id,
                                                                        leadTime: e.target.value || 0,
                                                                        requestToUpdate: updateSingleGroup
                                                                    }))
                                                                    setLeadTimeValue(null);
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        let newValue = e.target.value;
                                                        // Only allow numbers and minus sign at the start
                                                        if (/^\d*$/.test(newValue)) {
                                                            if (newValue.startsWith('0') && newValue.length > 1) {
                                                                newValue = newValue.replace(/^0+/, '');
                                                            }
                                                            setLeadTimeValue(newValue);
                                                        }
                                                    }}
                                                    disabled={productLeadTimeIsLoading() || access !== "edit"}
                                                    className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                                />
                                            </div>
                                        </div>
                                    </dd>
                                </div>

                                <div className="py-3 flex justify-between">
                                    <dt className="text-sm font-medium text-gray-500">Supplier</dt>
                                    <dd className="text-sm text-gray-900">
                                        <div className='flex flex-row gap-2 items-center'>
                                            {selectedSupplier && access === "edit" && (
                                                <XMarkIcon className='h-5 w-5 cursor-pointer' onClick={async () => {
                                                    await dispatch(updateVendorByProductRequest({
                                                        productId: product.id, 
                                                        newVendor: null,
                                                        requestToUpdate: updateSingleGroup
                                                    }))
                                                    setSelectedSupplier(null);
                                                }}/>
                                            )}
                                            <div className='w-52'>
                                                <SelectDropdown
                                                    key={selectedSupplier || "empty"}
                                                    disabled={getSuppliersToSelectIsLoading() || access !== "edit"}
                                                    options={vendorsToSelect}
                                                    value={selectedSupplier}
                                                    onChange={async (value) => {
                                                        if (access === "edit") {
                                                            if (value !== selectedSupplier) {
                                                                await dispatch(updateVendorByProductRequest({
                                                                    productId: product.id,
                                                                    newVendor: value,
                                                                    requestToUpdate: updateSingleGroup
                                                                }))
                                                            }
                                                            setSelectedSupplier(value);
                                                        }
                                                    }}
                                                    selectClassname='!bg-white !px-2 !py-1 !text-sm !rounded'
                                                    selectedValueClassname='truncate'
                                                    itemValueClassname='!text-sm'
                                                />
                                            </div>
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {product.description && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                                <p className="text-sm text-gray-700">
                                    {product.description}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'bom' && (
                    <div key={product}>
                        {/* <ProductBillOfMaterials
                            product={product}
                            access={access}
                            type={"ProductGroup"}
                        /> */}
                        <BOMVariant
                            key={product?.bomSkus?.length}
                            skus={product?.bomSkus}
                            access={access}
                            onClickVariantCallback={() => setGroupVariantModalData({
                                type: 'source-sku',
                                addVariantFunc: (variant) => handleAddBOMVariant(variant),
                                options: {}
                            })}
                            onClickRemoveCallback={(bomVariantSku) => handleRemoveBOMVariant(bomVariantSku)}
                            isDisabled={updateBOMVariantIsLoading()}
                            isLoading={updateBOMVariantIsLoading()}
                        />
                        <MOQ
                            product={product}
                            access={access}
                            type={"ProductGroup"}
                        />
                    </div>
                )}

                {activeTab === 'variants' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium text-gray-700">Product Variants</h3>
                            {arrayHasItems(variantsByGroup) && access === "edit" && (
                                <ConnectVariantButton
                                    onClickCallback={() => setIsVariantModalOpen(true)}
                                    isLoading={getVariantsByGroupIsLoading()}
                                />
                            )}
                        </div>
                        {arrayHasItems(variantsByGroup) ? (
                            <>
                                <div className="overflow-x-auto relative">
                                    {(deleteVariantIsLoading() || addVariantIsLoading() || getVariantsByGroupIsLoading()) && <OverlayGrayBackground />}
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                {access === "edit" && (
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {(variantsByGroup || [])?.map((variant) => (
                                                <tr key={variant.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2 text-sm text-gray-900">{variant.name}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-500">{variant.sku}</td>
                                                    <td className="px-4 py-2 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(variant.status)}`}>
                                                            {enumInventoryPhasedStatus[variant.status]}
                                                        </span>
                                                    </td>
                                                    {access === "edit" && (
                                                        <td className="px-4 py-2 text-sm text-gray-500">
                                                            <div className="flex items-center space-x-3">
                                                                {/* <button
                                                                    onClick={() => {
                                                                        // Need to think about logic when filters at the page is available
                                                                        onClickSidebarVariantView(variant)
                                                                    }}
                                                                    className="text-blue-600 hover:text-blue-900 focus:outline-none"
                                                                    aria-label={`View variant ${variant.name}`}
                                                                >
                                                                    <EyeIcon className="h-5 w-5" />
                                                                </button> */}
                                                                <button
                                                                    onClick={() => {
                                                                        handleDeleteVariant(variant.id)
                                                                    }}
                                                                    className="text-red-600 hover:text-red-900 focus:outline-none"
                                                                    aria-label={`Disconnect variant ${variant.name}`}
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8 border border-gray-200 rounded-md">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No variants</h3>
                                <p className="mt-1 text-sm text-gray-500">This group doesn't have any variants.</p>
                                {access === "edit" && (
                                    <div className="mt-6">
                                        <button 
                                            onClick={() => {
                                                setIsVariantModalOpen(true)
                                            }}
                                            disabled={getVariantsByGroupIsLoading()}
                                            type="button" 
                                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                            Connect variant
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* {activeTab === 'instructions' && (
                    <div>
                        <ProductInstructions
                            product={product}
                            access={access}
                        />
                    </div>
                )} */}

            {isVariantModalOpen && (
                <VariantModal
                    isOpen={access === "edit" && isVariantModalOpen}
                    onClose={() => {
                        setIsVariantModalOpen(false);
                    }}
                    onAddVariant={handleAddVariant}
                />
            )}
            {groupVariantModalData && (
                <VariantModal
                    isOpen={access === "edit" && groupVariantModalData}
                    onClose={() => {
                        setGroupVariantModalData(null);
                    }}
                    onAddVariant={(data) => groupVariantModalData?.addVariantFunc?.(data)}
                    {...groupVariantModalData?.options}
                />
            )}
        </div>
    );
};

export default ProductSidebar; 